/**
 * web boot recovery: web-capable boot-stall recovery decision.
 *
 * The only boot recovery today — `routes/room/timeout-watchdog.tsx` — is
 * Electron-gated (`ElectronAPI.restart()`), so a web boot that wedges (e.g. the
 * cloud LiveKit transport connect never completes, so `#livekit` never attaches
 * and the readiness query never resolves) leaves the BootOverlay up FOREVER with
 * no recovery but a manual refresh. This decides when a stuck overlay should be
 * converted into the existing recoverable error screen.
 *
 * Gated on NO FORWARD PROGRESS (`msSinceLastBootProgress`, logger/forward.ts) —
 * the same signal the Electron watchdog uses — so a slow-but-valid boot that is
 * still transitioning phases is left alone and only a genuinely stalled boot is
 * surfaced. Pure + exported for unit-testability (mirrors auth/room-selection.ts).
 * Per rule: think-in-systems (ai.knowledge/global).
 */

// Generous deadline: longer than the Electron TimeoutWatchdog (17s) and the
// getStatus probe timeout (10s), so on Electron the existing restart path acts
// first and a normal web boot (a few seconds) never trips this. A boot still
// stuck this long after its last phase transition is unambiguously wedged.
export const BOOT_STALL_DEADLINE_MS = 45_000;

/**
 * Decide what the boot-stall watchdog should do when its timer fires.
 *
 * @param msSinceProgress - `msSinceLastBootProgress()`: ms since the last real
 *   boot-phase transition, or `null` if the boot never advanced a phase.
 * @param deadlineMs - staleness ceiling.
 * @returns `'surface'` to raise the recoverable error, `'rearm'` to wait longer
 *   because the boot is still advancing.
 */
export const bootStallDecision = (
  msSinceProgress: number | null,
  deadlineMs: number = BOOT_STALL_DEADLINE_MS
): 'surface' | 'rearm' => {
  // null = never advanced (the genuine wedge the watchdog exists to recover);
  // >= deadline = stopped advancing. Either is a stall.
  if (msSinceProgress === null || msSinceProgress >= deadlineMs) {
    return 'surface';
  }

  // Boot advanced recently — still making progress, give it another window.
  return 'rearm';
};
